import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("auth_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Run once on mount to hydrate session from stored token. Deps intentionally empty.
    const token = localStorage.getItem("auth_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me").then((r) => {
      setUser((current) => {
        // Keep cached profile fields if an older backend omits optional tenant data.
        const merged = { ...(current || {}), ...(r.data || {}) };
        localStorage.setItem("auth_user", JSON.stringify(merged));
        return merged;
      });
    }).catch((err) => {
      console.warn("Session hydration failed:", err?.response?.status, err?.response?.data?.detail || err.message);
      if (err?.response?.status === 401) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
        setUser(null);
      }
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email: email.trim(), password });
    localStorage.setItem("auth_token", data.token);
    localStorage.setItem("auth_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, full_name) => {
    const { data } = await api.post("/auth/register", { email: email.trim(), password, full_name: full_name.trim() });
    localStorage.setItem("auth_token", data.token);
    localStorage.setItem("auth_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, register, logout }), [user, loading, login, register, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
